import { getConfig } from "../../config/env.config";

interface WebSocketMessage {
  type: "message" | "status" | "error";
  payload: any;
}

class WebSocketService {
  private static instance: WebSocketService;
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout = 1000; // Start with 1 second
  private messageQueue: string[] = [];
  private isConnected = false;
  private listeners: ((message: WebSocketMessage) => void)[] = [];

  private constructor() {
    // Private constructor for singleton
  }

  public static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  public connect(businessId: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    const { wsUrl } = getConfig();
    if (!wsUrl) {
      console.error("WebSocket URL not configured");
      return;
    }

    try {
      this.ws = new WebSocket(`${wsUrl}?businessId=${businessId}`);
      this.setupWebSocketHandlers();
    } catch (error) {
      console.error("Failed to establish WebSocket connection:", error);
      this.handleReconnection();
    }
  }

  private setupWebSocketHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      console.log("WebSocket connection established");
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.reconnectTimeout = 1000;
      this.flushMessageQueue();
    };

    this.ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        this.notifyListeners(message);
      } catch (error) {
        console.error("Failed to parse WebSocket message:", error);
      }
    };

    this.ws.onclose = () => {
      console.log("WebSocket connection closed");
      this.isConnected = false;
      this.handleReconnection();
    };

    this.ws.onerror = (error) => {
      console.error("WebSocket error:", error);
      this.isConnected = false;
    };
  }

  private handleReconnection(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error("Max reconnection attempts reached");
      return;
    }

    setTimeout(() => {
      this.reconnectAttempts++;
      this.reconnectTimeout *= 2; // Exponential backoff
      this.connect(this.getCurrentBusinessId());
    }, this.reconnectTimeout);
  }

  private getCurrentBusinessId(): string {
    // This should be implemented to get the current business ID from your app's state
    return "";
  }

  public send(message: any): void {
    const stringifiedMessage = JSON.stringify(message);

    if (this.isConnected && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(stringifiedMessage);
    } else {
      this.messageQueue.push(stringifiedMessage);
    }
  }

  private flushMessageQueue(): void {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      if (message && this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(message);
      }
    }
  }

  public addListener(callback: (message: WebSocketMessage) => void): void {
    this.listeners.push(callback);
  }

  public removeListener(callback: (message: WebSocketMessage) => void): void {
    this.listeners = this.listeners.filter((listener) => listener !== callback);
  }

  private notifyListeners(message: WebSocketMessage): void {
    this.listeners.forEach((listener) => listener(message));
  }

  public disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.isConnected = false;
    this.listeners = [];
    this.messageQueue = [];
  }

  public getConnectionStatus(): boolean {
    return this.isConnected;
  }
}

export default WebSocketService;
